import {useState} from 'react'
import {ViewMode} from './CommentsContext'
import {getCurrentUserLogin} from '@github-ui/client-env'

export const CommentsSort = {
  NewestFirst: 'newest-first',
  OldestFirst: 'oldest-first',
  DiffOrder: 'diff-order',
} as const

export type CommentsSort = (typeof CommentsSort)[keyof typeof CommentsSort]

/**
 * The state values of the comments filter
 */
export interface CommentsFilterState {
  hideResolvedThreads: boolean
  hideOutdatedThreads: boolean
  filterText: string
  selectedAuthor?: string
  /**
   * Sort controls both the direction of the items and how they are displayed:
   *  - `NewestFirst` and `OldestFirst` render review threads under review summaries along with issue comments
   *  - `DiffOrder` renders flat list of review threads, because review summaries and issue comments have no diff position
   */
  sort: CommentsSort
}

interface UseCommentsFilterStateResult {
  filterState: CommentsFilterState
  /** Set the filter state for the current view mode. */
  setCurrentFilterState: React.Dispatch<React.SetStateAction<CommentsFilterState>>
}

export const defaultFilterStates = (): Record<ViewMode, CommentsFilterState> => ({
  // "conversation" / "all"
  [ViewMode.IssueComments]: {
    filterText: '',
    hideResolvedThreads: false,
    hideOutdatedThreads: false,
    sort: CommentsSort.OldestFirst,
  },
  // "review" / "unresolved"
  [ViewMode.ThreadPreviews]: {
    filterText: '',
    hideResolvedThreads: true,
    hideOutdatedThreads: false,
    sort: CommentsSort.DiffOrder,
  },
  // "mine"
  [ViewMode.Mine]: {
    filterText: '',
    hideResolvedThreads: false,
    hideOutdatedThreads: false,
    sort: CommentsSort.OldestFirst,
    selectedAuthor: getCurrentUserLogin(),
  },
})

/** Tracks the state of comments filters. */
export function useCommentsFilterState(viewMode: ViewMode): UseCommentsFilterStateResult {
  // filter state for each view mode is tracked separately
  const [filterStates, setFilterStates] = useState(defaultFilterStates)

  const setViewModeFilterState = (mode: ViewMode, updatedState: React.SetStateAction<CommentsFilterState>) =>
    setFilterStates(previous => {
      const newState = typeof updatedState === 'function' ? updatedState(previous[mode]) : updatedState
      return {...previous, [mode]: newState}
    })

  const setCurrentFilterState = (filterState: React.SetStateAction<CommentsFilterState>) =>
    setViewModeFilterState(viewMode, filterState)

  return {
    filterState: filterStates[viewMode],
    setCurrentFilterState,
  } as const
}
